import React, { useCallback, useMemo } from "react";
import FilterBase from "../../components/Common/FilterBase";
import TableFilters from "../../components/Common/TableFilters";
import { useTableSearch } from "../../helpers/hooks";
import SearchBox from "../../components/Common/SearchBox";
import SubmenuItems from "../../components/Header/SubmenuItems";
import RankButton from "../../components/Common/Ranking/RankButton";
import { useSelector } from "react-redux";
import { createMap } from "reactcoregk/utils";
import { CAPABILITIES, RANK_ENTITY } from "../../constants/enums";
import { HiveIcons } from "../../assets/icons";
import { ActorIdFilter, CvesFilter, DateRangeFilter } from "./filters";
import { TextFilter } from "components/Common/TextFilter";
import { createOptions, generateOptionsForDistinctValues } from "helpers/utils";

function getActors(state) {
  return createMap(state.Actor.getAllPageable.all, "actorId");
}

const Menu = ({
  selected,
  handleCreateNew,
  distinctValues,
  verticals,
  countries,
  openSettings,
}) => {
  const [query, handleChange, syncQuery] = useTableSearch();

  const filterOptions = useMemo(() => {
    const { distinctMotivations } = distinctValues;

    const motivationsOptions = generateOptionsForDistinctValues(
      distinctMotivations ?? []
    );
    const verticalOptions = createOptions(verticals, "name", "name");

    const originsOptions = createOptions(countries, "name", "name");
    return [
      {
        customEl: (locationSearch, setLocationSearch) => (
          <ActorIdFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
          />
        ),
      },
      {
        customEl: (locationSearch, setLocationSearch, handleChange) => (
          <TextFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            filterKey="actorName"
            label="Actor Name"
            fullWidth
          />
        ),
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <TextFilter
            fullWidth
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            filterKey="actorType"
            label="Actor Type"
          />
        ),
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <DateRangeFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            fromFilterKey={"firstActivityDateFrom"}
            toFilterKey={"firstActivityDateTo"}
            fromLabel={"From First Activity Date"}
            toLabel={"To First Activity Date"}
          />
        ),
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <DateRangeFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            fromFilterKey={"lastActivityDateFrom"}
            toFilterKey={"lastActivityDateTo"}
            fromLabel={"From Last Activity Date"}
            toLabel={"To Last Activity Date"}
          />
        ),
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <TextFilter
            fullWidth
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            filterKey="knownAs"
            label="Known As"
          />
        ),
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <CvesFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
          />
        ),
      },

      {
        label: "Capabilities",
        value: "capabilities",
        options: CAPABILITIES,
        multiple: true,
      },

      {
        label: "Origins",
        value: "origin",
        options: originsOptions,
        multiple: true,
      },

      {
        label: "Motivations",
        value: "motivations",
        options: motivationsOptions,
        multiple: true,
      },

      {
        label: "Targeted Locations",
        value: "targetedLocations",
        options: originsOptions,
        multiple: true,
      },

      {
        label: "Targeted Industries",
        value: "targetIndustries",
        options: verticalOptions,
        multiple: true,
      },

      {
        customEl: (locationSearch, setLocationSearch) => (
          <DateRangeFilter
            locationSearch={locationSearch}
            setLocationSearch={setLocationSearch}
            fromFilterKey={"lastUpdatedAtFrom"}
            toFilterKey={"lastUpdatedAtTo"}
            fromLabel={"From Last Updated Date"}
            toLabel={"To Last Updated Date"}
          />
        ),
      },
    ];
  }, [distinctValues]);

  // get all the loaded actors
  const allActors = useSelector(getActors);

  const items = selected.map((id) => ({
    id,
    entityName: RANK_ENTITY.ACTORS,
    name: allActors.get(id)?.data?.name,
  }));

  const menuItems = [
    {
      pushRight: true,
      customEl: (
        <RankButton
          selected={items}
          entityName={"Actor"}
          rankEntity={RANK_ENTITY.ACTORS}
        />
      ),
    },
    {
      customEl: (
        <SearchBox
          query={query}
          handleChange={handleChange}
          syncQuery={syncQuery}
        />
      ),
    },
    {
      customEl: (
        <FilterBase>
          <TableFilters filterOptions={filterOptions} />
        </FilterBase>
      ),
    },
    {
      divider: true,
    },
    {
      title: "New Actor",
      icon: <HiveIcons.AddOutlined />,
      onClick: handleCreateNew,
    },
    {
      title: "Customize View",
      onClick: openSettings,
    },
  ];

  return <SubmenuItems items={menuItems} />;
};

export default Menu;
